/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

import styles from '@app/components/modals/tabs/components/AudioLevelMeter.module.css';
import {useEffect, useRef} from 'react';

interface AudioLevelMeterProps {
	level: number;
	thresholdPercent?: number;
	showThreshold?: boolean;
}

function getMeterColor(level: number): string {
	if (level < 0.6) return 'hsl(142, 71%, 45%)';
	if (level < 0.85) return 'hsl(48, 96%, 53%)';
	return 'hsl(0, 72%, 51%)';
}

export function AudioLevelMeter({level, thresholdPercent, showThreshold = false}: AudioLevelMeterProps) {
	const fillRef = useRef<HTMLDivElement>(null);
	const markerRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (fillRef.current) {
			fillRef.current.style.width = `${Math.min(100, Math.max(0, level * 100))}%`;
			fillRef.current.style.backgroundColor = level > 0 ? getMeterColor(level) : 'var(--text-tertiary)';
		}
	}, [level]);

	useEffect(() => {
		if (markerRef.current && thresholdPercent != null) {
			markerRef.current.style.left = `${Math.min(100, Math.max(0, thresholdPercent))}%`;
		}
	}, [thresholdPercent]);

	return (
		<div className={styles.track}>
			<div ref={fillRef} className={styles.fill} />
			{showThreshold && <div ref={markerRef} className={styles.marker} aria-hidden="true" />}
		</div>
	);
}
